// Function index:
//   runMenu(user)            — the main menu loop. Shows Continue / New / Worlds /
//       Admin / Logout, opens the right-hand drawer, and resolves with the
//       HomeChoice the user picked.
//   openDrawer(title)        — DOM helper: reveal the drawer and clear its body
//       and error slot; returns { body, error }.
//   sectionTitle(text)       — DOM helper: a styled section heading inside the drawer.
//   openNewAdventure(ctl)    — drawer pane: themed-worlds picker (players never forge worlds).
//   openYourWorlds(ctl, isAdmin) — drawer pane: my parties, join-by-invite, + admin-only owner/create sections.

import {
  joinThemedWorld, listThemedWorlds, logout, myParties, myWorlds,
  type AuthUser,
} from '../../api.js';
import { renderParties, renderThemed, renderWorlds } from '../home/render.js';
import type { HomeChoice } from '../title.js';
import { showAccountScreen } from '../account.js';
import {
  buildCreatePartyForm, buildCreateWorldForm, buildJoinForm,
  type DrawerCtl,
} from './forms.js';

const $ = (id: string): HTMLElement => document.getElementById(id)!;

export function runMenu(user: AuthUser): Promise<HomeChoice> {
  const menu = $('title-menu');
  const auth = $('title-auth') as HTMLFormElement;
  const drawer = $('title-drawer');
  const footL = $('title-footer-left');
  const footR = $('title-footer-right');

  auth.hidden = true;
  menu.hidden = false;
  drawer.hidden = true;
  footL.textContent = `Signed in as ${user.display_name}`;
  footR.textContent = 'Rule Zero';

  const ac = new AbortController();
  const on = (el: HTMLElement, ev: string, fn: (e: Event) => void): void =>
    el.addEventListener(ev, fn, { signal: ac.signal });

  return new Promise<HomeChoice>((resolve) => {
    const choose = (c: HomeChoice): void => { ac.abort(); drawer.hidden = true; resolve(c); };
    const ctl: DrawerCtl = { choose, signal: ac.signal };

    const continueBtn = menu.querySelector<HTMLButtonElement>('[data-act="continue"]')!;
    const continueSub = $('menu-continue-sub');
    void myParties().then(({ parties }) => {
      if (parties.length === 0) return;
      const p = parties[0]!;
      continueSub.textContent = `${p.world_name} · ${p.party_name}`;
      continueBtn.hidden = false;
      on(continueBtn, 'click', () => choose({ kind: 'play', partyId: p.party_id }));
    });

    on(menu.querySelector<HTMLButtonElement>('[data-act="new"]')!, 'click', () => openNewAdventure(ctl));
    on(menu.querySelector<HTMLButtonElement>('[data-act="worlds"]')!, 'click', () => openYourWorlds(ctl, user.role === 'admin'));

    const accountBtn = menu.querySelector<HTMLButtonElement>('[data-act="account"]')!;
    accountBtn.hidden = false;
    on(accountBtn, 'click', async () => {
      await showAccountScreen(user);
    });

    const adminBtn = menu.querySelector<HTMLButtonElement>('[data-act="admin"]')!;
    if (user.role === 'admin') {
      adminBtn.hidden = false;
      on(adminBtn, 'click', () => choose({ kind: 'admin' }));
    } else {
      adminBtn.hidden = true;
    }

    on(menu.querySelector<HTMLButtonElement>('[data-act="logout"]')!, 'click', async () => {
      await logout();
      location.reload();
    });

    on($('drawer-close'), 'click', () => { drawer.hidden = true; });
  });
}

export function openDrawer(title: string): { body: HTMLElement; error: HTMLElement } {
  const drawer = $('title-drawer');
  ($('drawer-title')).textContent = title;
  const body = $('drawer-body');
  const error = $('drawer-error');
  body.innerHTML = '';
  error.textContent = '';
  drawer.hidden = false;
  return { body, error };
}

export function sectionTitle(text: string): HTMLElement {
  const h = document.createElement('div');
  h.className = 'drawer-section-title';
  h.textContent = text;
  return h;
}

export async function openNewAdventure(ctl: DrawerCtl): Promise<void> {
  const { body, error } = openDrawer('New adventure');

  body.appendChild(sectionTitle('Step into a living world'));
  const themed = document.createElement('div');
  themed.className = 'party-list';
  themed.textContent = 'Loading…';
  body.appendChild(themed);

  try {
    const { themes } = await listThemedWorlds();
    renderThemed(themed, themes, async (theme) => {
      error.textContent = '';
      try {
        const { party_id } = await joinThemedWorld(theme);
        ctl.choose({ kind: 'play', partyId: party_id });
      } catch (err) {
        error.textContent = (err as Error).message;
      }
    });
  } catch (err) {
    themed.textContent = '';
    error.textContent = (err as Error).message;
  }
}

export async function openYourWorlds(ctl: DrawerCtl, isAdmin: boolean): Promise<void> {
  const { body, error } = openDrawer('Your worlds');

  body.appendChild(sectionTitle('Your parties'));
  const parties = document.createElement('div');
  parties.className = 'party-list';
  parties.textContent = 'Loading…';
  body.appendChild(parties);

  let worlds: HTMLElement | undefined;
  let partyFormHost: HTMLElement | undefined;
  if (isAdmin) {
    body.appendChild(sectionTitle('Worlds you own'));
    worlds = document.createElement('div');
    worlds.className = 'party-list';
    body.appendChild(worlds);
    partyFormHost = document.createElement('div');
    body.appendChild(partyFormHost);
  }

  body.appendChild(sectionTitle('Join by invite'));
  body.appendChild(buildJoinForm(ctl, error));

  if (isAdmin) {
    body.appendChild(sectionTitle('Create a world'));
    body.appendChild(buildCreateWorldForm(ctl, error));
  }

  try {
    const { parties: ps, member_cap } = await myParties();
    renderParties(parties, ps, member_cap, (partyId) => ctl.choose({ kind: 'play', partyId }));
    if (isAdmin) {
      const { worlds: ws } = await myWorlds();
      renderWorlds(
        worlds!, ws,
        (w) => ctl.choose({ kind: 'build', worldId: w.id }),
        (w) => { partyFormHost!.innerHTML = ''; partyFormHost!.appendChild(buildCreatePartyForm(w, ctl, error)); },
      );
    }
  } catch (err) {
    parties.textContent = '';
    error.textContent = (err as Error).message;
  }
}
